import React, { useState, useEffect } from 'react';
import RichTable from '../components/richTable/RichTable.jsx';
import tableHelpers from '../helpers/TableHelpers.js';
import Dropdown from 'react-bootstrap/Dropdown';
import BacktestResults from './BacktestResults.js';

function PlanWizard(props) {

    const [algos, setAlgos] = useState([]);
    const [filteredAlgos, setFilteredAlgos] = useState([]);
    const [allocations, setAllocations] = useState({});
    const [statYears, setStatYears] = useState(5);
    const [selectedSymbols, setSelectedSymbols] = useState([]);
    const [filters, setFilters] = useState({
        minWinRate: 50,
        minTrades: 10,
        minAvgProfit: 0.5,
        maxDrawdown: 100,
        minRobustness: 0,
        minWinLossRatio: 0,
        algoType: 'All',
        maxAlgos: 0,
        maxAlgosPerSymbol: 0,
        minAlgosPerSymbol: 0,
        sortBy: 'avg_profit',
        maxAvgDuration: 0
    });
    const [batchUpdate, setBatchUpdate] = useState({
        symbol: 'All',
        algoType: 'All',
        allocation: 0
    });
    const [backtestResults, setBacktestResults] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [isTestRunning, setIsTestRunning] = useState(false);
    const [availableSymbols, setAvailableSymbols] = useState([]);
    const [availableAlgoTypes, setAvailableAlgoTypes] = useState([]);
    const [filterDuplicates, setFilterDuplicates] = useState(false);

    useEffect(() => {
        (async function () {
            fetch(`/api/algoStats?planId=` + props.planId + "&all=1")
                .then(res => res.json())
                .then(text => {
                    setAlgos(text);
                    const syms = [...new Set(text.map(a => a.trade_symbol))].sort();
                    setAvailableSymbols(syms);
                    const types = [...new Set(text.map(a => a.algo_type))].sort();
                    setAvailableAlgoTypes(types);
                });
        })();
    }, [props.planId]);

    useEffect(() => {
        let result = algos.filter(a => a.years === statYears);

        if (selectedSymbols.length > 0) {
            result = result.filter(a => selectedSymbols.includes(a.trade_symbol));
        }

        if (filters.algoType !== 'All') {
            result = result.filter(a => a.algo_type === filters.algoType);
        }

        result = result.filter(a =>
            a.winning_percentage >= filters.minWinRate &&
            a.count >= filters.minTrades &&
            a.avg_profit >= filters.minAvgProfit &&
            a.max_drawdown <= filters.maxDrawdown &&
            a.algo_robustness_overall >= filters.minRobustness &&
            (a.avg_loss === 0 || Math.abs(a.avg_win / a.avg_loss) >= filters.minWinLossRatio) &&
            (filters.maxAvgDuration === 0 || a.avg_duration <= filters.maxAvgDuration)
        );

        // Sort
        result.sort((a, b) => {
            if (filters.sortBy === 'max_drawdown') {
                return a[filters.sortBy] - b[filters.sortBy];
            }
            return b[filters.sortBy] - a[filters.sortBy];
        });

        // Filter Duplicates
        if (filterDuplicates) {
            const seen = new Set();
            result = result.filter(a => {
                const normalizedName = a.display_name.replace(/ v\d+$/, '').trim();
                if (seen.has(normalizedName)) return false;
                seen.add(normalizedName);
                return true;
            });
        }

        // Min Algos Per Symbol
        if (filters.minAlgosPerSymbol > 0) {
            const symbolCounts = {};
            result.forEach(a => {
                symbolCounts[a.trade_symbol] = (symbolCounts[a.trade_symbol] || 0) + 1;
            });
            result = result.filter(a => symbolCounts[a.trade_symbol] >= filters.minAlgosPerSymbol);
        }

        // Max Algos Per Symbol
        if (filters.maxAlgosPerSymbol > 0) {
            const symbolCounts = {};
            const newResult = [];
            result.forEach(a => {
                if (!symbolCounts[a.trade_symbol]) symbolCounts[a.trade_symbol] = 0;
                if (symbolCounts[a.trade_symbol] < filters.maxAlgosPerSymbol) {
                    newResult.push(a);
                    symbolCounts[a.trade_symbol]++;
                }
            });
            result = newResult;
        }

        // Max Total Algos
        if (filters.maxAlgos > 0) {
            result = result.slice(0, filters.maxAlgos);
        }

        setFilteredAlgos(result);
    }, [algos, statYears, selectedSymbols, filters, filterDuplicates]);

    function handleAllocationChange(algoId, value) {
        setAllocations(prev => ({ ...prev, [algoId]: Number(value) }));
    }

    function handleBatchUpdate() {
        const newAllocations = { ...allocations };
        filteredAlgos.forEach(a => {
            if ((batchUpdate.symbol === 'All' || a.trade_symbol === batchUpdate.symbol) &&
                (batchUpdate.algoType === 'All' || a.algo_type === batchUpdate.algoType)) {
                newAllocations[a.algoId] = Number(batchUpdate.allocation);
            }
        });
        setAllocations(newAllocations);
    }

    function toggleSymbol(sym) {
        if (selectedSymbols.includes(sym)) {
            setSelectedSymbols(selectedSymbols.filter(s => s !== sym));
        } else {
            setSelectedSymbols([...selectedSymbols, sym]);
        }
    }

    async function runBacktest() {
        if (!isTestRunning) {
            let fullStartDate = new Date(2014, 0, 1);
            let fullEndDate = new Date();
            let startAmount = 100000;
            let finalAllocations = {};

            filteredAlgos.forEach(a => {
                if (allocations[a.algoId] > 0) {
                    finalAllocations[a.algoId] = allocations[a.algoId];
                }
            });

            if (Object.keys(finalAllocations).length === 0) {
                alert("No allocations set.");
                return;
            }

            const backtestTradesUrl = `/api/backtestRunComplete?planId=` + props.planId +
                "&startDate=" + fullStartDate.toLocaleDateString() +
                "&endDate=" + fullEndDate.toLocaleDateString() +
                "&startAmount=" + startAmount +
                "&activeTradesOnly=0" +
                "&liveTradesOnly=0" +
                "&deleteResults=0" +
                "&allocationStrategyId=4" +
                "&isOfficial=0";

            setIsTestRunning(true);
            setBacktestResults(null);
            setChartData([]);

            try {
                let response = await fetch(backtestTradesUrl, { method: 'POST', body: JSON.stringify(finalAllocations) });
                let results = await response.json();

                if (results.dailyResults) {
                    const tempChartData = [];
                    results.dailyResults.forEach(day => tempChartData.push([new Date(day.date), day.total, day.baseline]));
                    setChartData(tempChartData);
                }
                setBacktestResults(results);
            } catch (e) {
                console.error(e);
                alert("Error running backtest");
            } finally {
                setIsTestRunning(false);
            }
        }
    }

    const allocationFormatter = (cell, row) => {
        return <input type="number" style={{ width: '60px' }} value={allocations[row.algoId] || 0} onChange={(e) => handleAllocationChange(row.algoId, e.target.value)} />
    }

    return (
        <div style={{ padding: '20px' }}>
            <h4>Plan Wizard</h4>
            <div className="row">
                <div className="col-md-3">
                    <h5>Filters</h5>
                    <div>
                        <label>Stats Timeframe:</label>
                        <Dropdown onSelect={(e) => setStatYears(Number(e))}>
                            <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-basic">
                                {statYears === 0 ? "Live Trades" : statYears + " Years"}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item eventKey={0}>Live Trades</Dropdown.Item>
                                <Dropdown.Item eventKey={1}>1 Year</Dropdown.Item>
                                <Dropdown.Item eventKey={2}>2 Years</Dropdown.Item>
                                <Dropdown.Item eventKey={3}>3 Years</Dropdown.Item>
                                <Dropdown.Item eventKey={5}>5 Years</Dropdown.Item>
                                <Dropdown.Item eventKey={10}>10 Years</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Symbols:</label>
                        <Dropdown onSelect={(e) => toggleSymbol(e)}>
                            <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-basic">
                                Add Symbol
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {availableSymbols.map(s => <Dropdown.Item key={s} eventKey={s}>{s}</Dropdown.Item>)}
                            </Dropdown.Menu>
                        </Dropdown>
                        <div style={{ marginTop: '5px' }}>
                            {selectedSymbols.map(s => (
                                <span key={s} style={{ backgroundColor: '#e0e0e0', padding: '2px 5px', margin: '2px', borderRadius: '4px', fontSize: '0.9em' }}>
                                    {s} <span style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => toggleSymbol(s)}>x</span>
                                </span>
                            ))}
                        </div>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Algo Type:</label>
                        <Dropdown onSelect={(e) => setFilters({ ...filters, algoType: e })}>
                            <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-basic">
                                {filters.algoType}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item eventKey="All">All</Dropdown.Item>
                                {availableAlgoTypes.map(t => <Dropdown.Item key={t} eventKey={t}>{t}</Dropdown.Item>)}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Min Win %:</label>
                        <input type="number" className="form-control form-control-sm" value={filters.minWinRate} onChange={(e) => setFilters({ ...filters, minWinRate: Number(e.target.value) })} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Min Trades:</label>
                        <input type="number" className="form-control form-control-sm" value={filters.minTrades} onChange={(e) => setFilters({ ...filters, minTrades: Number(e.target.value) })} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Min Avg Profit %:</label>
                        <input type="number" className="form-control form-control-sm" value={filters.minAvgProfit} onChange={(e) => setFilters({ ...filters, minAvgProfit: Number(e.target.value) })} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Max Drawdown %:</label>
                        <input type="number" className="form-control form-control-sm" value={filters.maxDrawdown} onChange={(e) => setFilters({ ...filters, maxDrawdown: Number(e.target.value) })} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Min Robustness:</label>
                        <input type="number" className="form-control form-control-sm" value={filters.minRobustness} onChange={(e) => setFilters({ ...filters, minRobustness: Number(e.target.value) })} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Min Win/Loss Ratio:</label>
                        <input type="number" className="form-control form-control-sm" value={filters.minWinLossRatio} onChange={(e) => setFilters({ ...filters, minWinLossRatio: Number(e.target.value) })} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Max Avg Duration:</label>
                        <input type="number" className="form-control form-control-sm" value={filters.maxAvgDuration} onChange={(e) => setFilters({ ...filters, maxAvgDuration: Number(e.target.value) })} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <input type="checkbox" checked={filterDuplicates} onChange={(e) => setFilterDuplicates(e.target.checked)} /> Filter Duplicates
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Sort By:</label>
                        <Dropdown onSelect={(e) => setFilters({ ...filters, sortBy: e })}>
                            <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-basic">
                                {filters.sortBy === 'avg_profit' ? 'Avg Profit' :
                                    filters.sortBy === 'winning_percentage' ? 'Win %' :
                                        filters.sortBy === 'algo_robustness_overall' ? 'Robustness' :
                                            filters.sortBy === 'count' ? 'Trades' :
                                                filters.sortBy === 'max_drawdown' ? 'Max Drawdown' : filters.sortBy}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item eventKey="avg_profit">Avg Profit</Dropdown.Item>
                                <Dropdown.Item eventKey="winning_percentage">Win %</Dropdown.Item>
                                <Dropdown.Item eventKey="algo_robustness_overall">Robustness</Dropdown.Item>
                                <Dropdown.Item eventKey="count">Trades</Dropdown.Item>
                                <Dropdown.Item eventKey="max_drawdown">Max Drawdown</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Max Algos (Total):</label>
                        <input type="number" className="form-control form-control-sm" value={filters.maxAlgos} onChange={(e) => setFilters({ ...filters, maxAlgos: Number(e.target.value) })} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Max Algos (Per Symbol):</label>
                        <input type="number" className="form-control form-control-sm" value={filters.maxAlgosPerSymbol} onChange={(e) => setFilters({ ...filters, maxAlgosPerSymbol: Number(e.target.value) })} />
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <label>Min Algos (Per Symbol):</label>
                        <input type="number" className="form-control form-control-sm" value={filters.minAlgosPerSymbol} onChange={(e) => setFilters({ ...filters, minAlgosPerSymbol: Number(e.target.value) })} />
                    </div>
                </div>
                <div className="col-md-9">
                    <h5>Matching Algos ({filteredAlgos.length})</h5>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '10px', marginBottom: '10px', borderRadius: '5px' }}>
                        <h6>Batch Update Allocations</h6>
                        <div className="sameline">
                            <Dropdown onSelect={(e) => setBatchUpdate({ ...batchUpdate, symbol: e })}>
                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                    Symbol: {batchUpdate.symbol}
                                </Dropdown.Toggle>
                                <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <Dropdown.Item eventKey="All">All</Dropdown.Item>
                                    {availableSymbols.map(s => <Dropdown.Item key={s} eventKey={s}>{s}</Dropdown.Item>)}
                                </Dropdown.Menu>
                            </Dropdown>
                            &nbsp;
                            <Dropdown onSelect={(e) => setBatchUpdate({ ...batchUpdate, algoType: e })}>
                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                    Type: {batchUpdate.algoType}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item eventKey="All">All</Dropdown.Item>
                                    {availableAlgoTypes.map(t => <Dropdown.Item key={t} eventKey={t}>{t}</Dropdown.Item>)}
                                </Dropdown.Menu>
                            </Dropdown>
                            &nbsp;
                            <input type="number" style={{ width: '80px' }} placeholder="%" value={batchUpdate.allocation} onChange={(e) => setBatchUpdate({ ...batchUpdate, allocation: e.target.value })} />
                            &nbsp;
                            <button className="btn btn-sm btn-primary" onClick={handleBatchUpdate}>Apply</button>
                        </div>
                    </div>

                    <RichTable
                        data={filteredAlgos}
                        className="table-striped table-hover table-condensed"
                        pageSize={50}
                        mappers={[
                            { title: 'Algo', field: 'display_name', sorters: true },
                            { title: 'Symbol', field: 'trade_symbol', sorters: true },
                            { title: 'Type', field: 'algo_type', sorters: true },
                            { title: 'Win %', field: 'winning_percentage', formatter: tableHelpers.percentFormatter, sorters: true },
                            { title: 'Trades', field: 'count', formatter: tableHelpers.intFormatter, sorters: true },
                            { title: 'Avg P/L', field: 'avg_profit', formatter: tableHelpers.percentFormatter, sorters: true },
                            { title: 'Max DD', field: 'max_drawdown', formatter: tableHelpers.percentFormatter, sorters: true },
                            { title: 'Robustness', field: 'algo_robustness_overall', formatter: tableHelpers.decimalFormatter, sorters: true },
                            { title: 'Allocation %', field: 'algoId', formatter: allocationFormatter }
                        ]}
                    />

                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                        <button className="btn btn-success btn-lg" onClick={runBacktest} disabled={isTestRunning}>
                            {isTestRunning ? 'Running...' : 'Run Backtest'}
                        </button>
                    </div>

                    {backtestResults && (
                        <div style={{ marginTop: '20px' }}>
                            <h5>Backtest Results</h5>
                            <BacktestResults backtestResults={backtestResults} mobileView={props.mobileView} chartData={chartData} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PlanWizard;
